import React, { useCallback, useEffect, useState } from 'react';
import api from '@/api';
import { useOrgStore } from '@/store/orgStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Users, Settings, Shield, Plus, Mail, Copy, Trash2, Clock, CheckCircle, Key, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function SettingsPage() {
  const [orgUsers, setOrgUsers] = useState([]);
  const [stages, setStages] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviteLink, setInviteLink] = useState('');
  const [inviteSaving, setInviteSaving] = useState(false);
  const { currentOrgId, currentOrgName } = useOrgStore();
  const { user } = useAuth();

  const fetchData = useCallback(() => {
    if (!currentOrgId) return;
    api.get('/settings/users').then((r) => setOrgUsers(r.data)).catch(console.error);
    api.get('/pipeline-stages').then((r) => setStages(r.data)).catch(console.error);
    api.get('/organizations').then((r) => setOrgs(r.data)).catch(console.error);
    api.get('/settings/invitations').then((r) => setInvitations(r.data)).catch(console.error);
    api.get('/roles').then((r) => setRoles(r.data)).catch(console.error);
  }, [currentOrgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const currentOrg = orgs.find((o) => o.id === currentOrgId);

  const handleUpdateOrg = async (field, value) => {
    try {
      await api.put(`/organizations/${currentOrgId}`, { [field]: value });
      toast.success('Organizacion actualizada');
      api.get('/organizations').then((r) => setOrgs(r.data));
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail) return;
    setInviteSaving(true);
    try {
      const { data } = await api.post('/settings/invite', { email: inviteEmail, role: inviteRole });
      setInviteLink(data.invite_link);
      toast.success(`Invitacion creada para ${inviteEmail}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar invitacion');
    }
    setInviteSaving(false);
  };

  const handleDeleteInvite = async (id) => {
    try {
      await api.delete(`/settings/invitations/${id}`);
      toast.success('Invitacion eliminada');
      fetchData();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Enlace copiado al portapapeles');
  };

  return (
    <div data-testid="settings-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Configuracion</h1>
        <p className="text-sm text-[#475569] mt-1">Gestiona tu organizacion, usuarios y pipeline</p>
      </div>
      <div className="mb-4">
        <Link to="/changelog" className="inline-flex min-h-11 md:min-h-0 items-center gap-2 rounded-md text-sm font-medium text-[#2563EB] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2">
          <FileText size={14} /> Ver changelog del sistema
        </Link>
      </div>

      <Tabs defaultValue="org" className="space-y-4">
        <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
          <TabsList className="flex h-auto w-max min-w-full justify-start bg-[#F1F5F9] lg:w-auto lg:min-w-0">
            <TabsTrigger value="org" data-testid="tab-org" className="min-h-9 whitespace-nowrap"><Building2 size={14} className="mr-1.5" /> Organizacion</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="min-h-9 whitespace-nowrap"><Users size={14} className="mr-1.5" /> Usuarios</TabsTrigger>
            <TabsTrigger value="roles" data-testid="tab-roles" className="min-h-9 whitespace-nowrap"><Key size={14} className="mr-1.5" /> Roles</TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="min-h-9 whitespace-nowrap"><Settings size={14} className="mr-1.5" /> Pipeline</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="org">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader><CardTitle className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Datos de la Organizacion</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Nombre</Label>
                  <div className="flex flex-col gap-2 mt-1 sm:flex-row">
                    <Input defaultValue={currentOrg?.name || ''} id="org-name" className="min-h-11 md:min-h-10 border-[#E2E8F0]" data-testid="settings-org-name" />
                    <Button variant="outline" onClick={() => { const value = document.getElementById('org-name')?.value; if (value) handleUpdateOrg('name', value); }} className="min-h-11 md:min-h-10 border-[#E2E8F0] shrink-0 focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2">Guardar</Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">RUT</Label>
                  <Input defaultValue={currentOrg?.rut || ''} className="min-h-11 md:min-h-10 border-[#E2E8F0] mt-1" readOnly />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Industria</Label>
                  <Input defaultValue={currentOrg?.industry || ''} className="min-h-11 md:min-h-10 border-[#E2E8F0] mt-1" readOnly />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Plan</Label>
                  <div className="mt-1">
                    <Badge variant="outline" className="bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20 text-sm font-medium px-3 py-1">{currentOrg?.plan === 'trial' ? 'Prueba Gratuita (14 dias)' : currentOrg?.plan || '-'}</Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <CardTitle className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Usuarios de {currentOrgName}</CardTitle>
                <Button onClick={() => { setInviteOpen(true); setInviteEmail(''); setInviteLink(''); setInviteRole('user'); }} className="w-full min-h-11 md:min-h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2 sm:w-auto" data-testid="invite-user-btn">
                  <Plus size={16} className="mr-2" /> Invitar Usuario
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {orgUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`user-row-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarFallback className="bg-[#2563EB] text-white text-xs font-semibold">{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#0F172A] truncate">{u.name}</p>
                        <p className="text-xs text-[#475569] truncate">{u.email}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`shrink-0 text-xs font-medium ${u.role === 'admin' ? 'bg-[#EFF6FF] text-[#2563EB] border-[#2563EB]/20' : 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'}`}>
                      <Shield size={12} className="mr-1" /> {u.role === 'admin' ? 'Administrador' : 'Usuario'}
                    </Badge>
                  </div>
                ))}
              </div>
              {invitations.filter((i) => i.status === 'pending').length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-bold uppercase tracking-widest text-[#94A3B8] mb-3">Invitaciones Pendientes</h3>
                  <div className="space-y-2">
                    {invitations.filter((i) => i.status === 'pending').map((inv) => (
                      <div key={inv.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-dashed border-[#E2E8F0] bg-[#F8FAFC]" data-testid={`invite-row-${inv.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 shrink-0 rounded-full bg-[#FFFBEB] flex items-center justify-center">
                            <Mail size={16} className="text-[#F59E0B]" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-[#0F172A] truncate">{inv.email}</p>
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className="text-xs bg-[#FFFBEB] text-[#B45309] border-[#F59E0B]/20"><Clock size={10} className="mr-1" /> Pendiente</Badge>
                              <span className="text-xs text-[#94A3B8]">{inv.role === 'admin' ? 'Admin' : 'Usuario'}</span>
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteInvite(inv.id)} className="h-11 w-11 md:h-9 md:w-9 shrink-0 text-[#EF4444] hover:text-[#DC2626] focus-visible:ring-2 focus-visible:ring-[#EF4444] focus-visible:ring-offset-2" data-testid={`delete-invite-${inv.id}`}>
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
              <DialogHeader>
                <DialogTitle className="font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Invitar Usuario</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Correo electronico</Label>
                  <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="usuario@empresa.cl" className="min-h-11 md:min-h-10 border-[#E2E8F0]" data-testid="invite-email-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-widest text-[#94A3B8]">Rol</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger className="min-h-11 md:min-h-10 border-[#E2E8F0]" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Usuario</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {inviteLink && (
                  <div className="p-3 bg-[#DCFCE7] border border-[#22C55E]/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle size={16} className="text-[#22C55E]" />
                      <span className="text-sm font-medium text-[#15803D]">Invitacion creada</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input value={inviteLink} readOnly className="min-h-11 md:min-h-10 text-xs border-[#22C55E]/30 bg-white" data-testid="invite-link-display" />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(inviteLink)} className="h-11 w-11 md:h-10 md:w-10 shrink-0 border-[#22C55E]/30 focus-visible:ring-2 focus-visible:ring-[#22C55E] focus-visible:ring-offset-2" data-testid="copy-invite-link">
                        <Copy size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setInviteOpen(false)} className="min-h-11 md:min-h-10 border-[#E2E8F0] focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2">Cerrar</Button>
                {!inviteLink && (
                  <Button onClick={handleInvite} disabled={inviteSaving || !inviteEmail} className="min-h-11 md:min-h-10 bg-[#2563EB] hover:bg-[#1D4ED8] text-white focus-visible:ring-2 focus-visible:ring-[#2563EB] focus-visible:ring-offset-2" data-testid="send-invite-btn">
                    {inviteSaving ? 'Enviando...' : 'Enviar Invitacion'}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="roles">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Roles y Permisos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {roles.map((role) => (
                  <div key={role.id || role.key} className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={`role-card-${role.key || role.id}`}>
                    <div className="flex items-center justify-between gap-3 p-4 bg-[#F8FAFC]">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 shrink-0 rounded-lg bg-[#EFF6FF] flex items-center justify-center">
                          <Shield size={18} className="text-[#2563EB]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[#0F172A] truncate">{role.name}</p>
                          <p className="text-xs text-[#475569] line-clamp-2">{role.description}</p>
                        </div>
                      </div>
                      {role.is_default && <Badge variant="outline" className="shrink-0 text-xs bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]">Predeterminado</Badge>}
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                        {role.permissions && Object.entries(role.permissions).map(([module, actions]) => (
                          <div key={module} className="text-xs">
                            <p className="font-bold uppercase tracking-wider text-[#94A3B8] mb-1">{module}</p>
                            <div className="flex flex-wrap gap-1">
                              {(Array.isArray(actions) ? actions : []).map((action) => (
                                <span key={action} className="inline-flex items-center px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#15803D] text-[10px] font-medium">
                                  {action}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pipeline">
          <Card className="border-[#E2E8F0] shadow-sm">
            <CardHeader><CardTitle className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Etapas del Pipeline</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stages.map((stage) => (
                  <div key={stage.id} className="flex items-center gap-3 p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`stage-row-${stage.id}`}>
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: stage.color }} />
                    <span className="text-sm font-medium text-[#0F172A] flex-1 min-w-0 truncate">{stage.name}</span>
                    <span className="text-xs text-[#94A3B8] shrink-0 tabular-nums">Orden: {stage.order}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
